// Support hub. Findable page that answers common questions and gives a contact
// path. Styled to match HowToPlay/ContestRules (MarketingLayout + Card sections).
// Copy is finalized (SAB support-page content pass). All internal links are
// relative react-router paths so they resolve on any host.
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Mail, BookOpen, FileText, HeartHandshake, MapPin } from 'lucide-react';
import MarketingLayout from '@/components/shared/MarketingLayout';

const SUPPORT_EMAIL = 'support@sabotagefantasy.com';

const QUICK_LINKS = [
  { label: 'How to Play', desc: 'Rules, scoring, and getting started', href: '/how-to-play', icon: BookOpen },
  { label: 'Contest Rules', desc: 'Official rules, prizes, and disputes', href: '/contest-rules', icon: FileText },
  { label: 'Responsible Gaming', desc: 'Tools, limits, and where to get help', href: '/responsible-gaming', icon: HeartHandshake },
  { label: 'State Availability', desc: 'Where you can play and age minimums', href: '/state-availability', icon: MapPin },
];

interface QA { q: string; a: React.ReactNode; }
const FAQ_GROUPS: { group: string; items: QA[] }[] = [
  {
    group: 'Getting started',
    items: [
      {
        q: 'How do I create or join a league?',
        a: (
          <>
            Browse open leagues from{' '}
            <Link to="/nfl/leagues/browse" className="text-primary hover:underline">Browse Leagues</Link>, or start your own.
            Full walkthrough on the{' '}
            <Link to="/how-to-play" className="text-primary hover:underline">How to Play</Link> page.
          </>
        ),
      },
      {
        q: 'How does scoring work?',
        a: (
          <>
            Sabotage Fantasy uses inverted "trash" scoring: you earn points from opponents' bad real-life stats
            (interceptions, fumbles, missed kicks, penalties) and lose points for good ones. See{' '}
            <Link to="/how-to-play" className="text-primary hover:underline">How to Play</Link> and{' '}
            <Link to="/contest-rules" className="text-primary hover:underline">Contest Rules</Link> for the full model.
          </>
        ),
      },
    ],
  },
  {
    group: 'Sabotage store',
    items: [
      {
        q: 'What are sabotage cards and how do I use them?',
        a: (
          <>
            In the sabotage store you buy specific cards at posted prices, then drop one on an opponent to wreck their week.
            Rarer cards have bigger effects. Cards are single-use and public, so your whole league sees what you played. You
            choose exactly which card to buy and who to drop it on.
          </>
        ),
      },
      {
        q: 'How many sabotages can I use?',
        a: (
          <>
            One per week. Some legendary cards are league-wide and affect everyone in your league that week, and only one
            league-wide legendary can be played per league per week.
          </>
        ),
      },
    ],
  },
  {
    group: 'Deposits & withdrawals',
    items: [
      {
        q: 'How do I add or withdraw funds?',
        a: (
          <>
            Manage your wallet from the Wallet section in your league settings. If a transaction needs manual review, the app
            will point you to <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>.
          </>
        ),
      },
      {
        q: 'How long do deposits and withdrawals take?',
        a: (
          <>
            Deposits are typically available in your wallet right away. Withdrawals are sent to your linked bank account and
            usually settle within a few business days.
          </>
        ),
      },
      {
        q: 'What is your refund policy?',
        a: (
          <>
            If a league does not fill, your buy-in is returned to your wallet. Sabotage card and credit purchases are final
            and non-refundable. For anything else, email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>{' '}
            and we will look at it case by case.
          </>
        ),
      },
      {
        q: 'What identity verification do I need?',
        a: (
          <>
            Before your first real-money transaction, you will complete identity verification through our provider, Persona.
            That includes a government-issued photo ID (driver's license, state ID, or passport), a selfie/liveness check
            matched against your ID, date-of-birth verification confirming you meet your state's minimum age, and confirmation
            of your residential address.
          </>
        ),
      },
    ],
  },
  {
    group: 'Eligibility & account',
    items: [
      {
        q: 'Where can I play, and what is the minimum age?',
        a: (
          <>
            Availability varies by state, check{' '}
            <Link to="/state-availability" className="text-primary hover:underline">State Availability</Link> for the current
            list. You must be at least 18 to play for real money, and 19 in Nebraska. States we may activate later could
            set different minimums.
          </>
        ),
      },
      {
        q: 'How do I reset my password?',
        a: (
          <>
            Use "Forgot password" on the sign-in screen. If the reset email does not arrive, email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>.
          </>
        ),
      },
      {
        q: 'How do I delete my account?',
        a: (
          <>
            Go to Settings then Delete Account in the app, or email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>.
          </>
        ),
      },
    ],
  },
  {
    group: 'Prizes & payouts',
    items: [
      {
        q: 'How are prizes and payouts determined?',
        a: (
          <>
            Prize pools follow your league's payout structure, set at league creation and visible in league settings. See{' '}
            <Link to="/contest-rules" className="text-primary hover:underline">Contest Rules</Link> for details.
          </>
        ),
      },
    ],
  },
];

export default function Support() {
  return (
    <MarketingLayout>
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground mb-2">Support</h1>
        <p className="text-sm text-muted-foreground m-0">
          Answers to common questions, links to our guides, and how to reach a human.
        </p>
      </div>

      {/* Contact */}
      <Card className="bg-card border-border">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <h2 className="text-base font-bold text-foreground m-0">Contact us</h2>
          </div>
          <p className="text-sm text-muted-foreground m-0">
            Email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary font-semibold hover:underline">{SUPPORT_EMAIL}</a>.
            We aim to respond within 4 hours. To help us resolve your issue fast, please include:
          </p>
          <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1 m-0">
            <li>The email address on your account</li>
            <li>Your league name (if it is league-specific)</li>
            <li>What happened, and what you expected instead</li>
            <li>Screenshots or the approximate time it occurred, if relevant</li>
          </ul>
        </CardContent>
      </Card>

      {/* Quick links to existing guides */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-foreground mb-4">Guides</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {QUICK_LINKS.map((l) => {
              const Icon = l.icon;
              return (
                <Link
                  key={l.href}
                  to={l.href}
                  className="flex items-start gap-3 rounded-lg border border-border p-3 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                >
                  <Icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <span>
                    <span className="block text-sm font-semibold text-foreground">{l.label}</span>
                    <span className="block text-xs text-muted-foreground">{l.desc}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* FAQ */}
      <Card className="bg-card border-border">
        <CardContent className="p-6 space-y-6">
          <h2 className="text-base font-bold text-foreground m-0">Frequently asked questions</h2>
          {FAQ_GROUPS.map((g) => (
            <section key={g.group} className="space-y-4">
              <h3 className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground m-0">{g.group}</h3>
              {g.items.map((item) => (
                <div key={item.q}>
                  <p className="text-sm font-semibold text-foreground m-0 mb-1">{item.q}</p>
                  <div className="text-sm text-muted-foreground leading-relaxed">{item.a}</div>
                </div>
              ))}
            </section>
          ))}
        </CardContent>
      </Card>

      {/* Responsible gaming */}
      <Card className="bg-card border-border">
        <CardContent className="p-6 space-y-2">
          <div className="flex items-center gap-2">
            <HeartHandshake className="h-5 w-5 text-primary" />
            <h2 className="text-base font-bold text-foreground m-0">Play responsibly</h2>
          </div>
          <p className="text-sm text-muted-foreground m-0">
            You must be 18 or older to play for real money (19 in Nebraska; states we may activate later could set
            different minimums). If you or someone you know has a gambling problem, call or text{' '}
            <strong className="text-foreground">1-800-GAMBLER</strong>. NY: text HOPENY (467369). AZ: 1-800-NEXT-STEP.
            More tools on our{' '}
            <Link to="/responsible-gaming" className="text-primary hover:underline">Responsible Gaming</Link> page.
          </p>
        </CardContent>
      </Card>
    </MarketingLayout>
  );
}
